import { Component, ViewChild, Injector, ElementRef } from '@angular/core';
import { Calendar } from 'primeng/calendar';
import { MessageService } from 'primeng/api';
import { RecebimentoExtraService } from '../service/recebimento-extra.service';
import { Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import {
  BaseResourceFormComponent, RecebimentoExtra,
  LoginContabil, EddyAutoComplete, ContaBancaria, FichaExtra,
  GlobalService, FuncaoService, DateFormatPipe
} from 'eddydata-lib';
import { ContaBancariaService } from 'administrativo-lib';
import { FichaExtraService } from '../../../ficha-extra/service/ficha-extra.service';

@Component({
  selector: 'app-recebimento-extra-form',
  templateUrl: './recebimento-extra-form.component.html'
})
export class RecebimentoExtraFormComponent extends BaseResourceFormComponent<RecebimentoExtra, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  @ViewChild('InputFocus') private inputField: Calendar;

  /**
   * Construtor com as injeções de dependencias
   */
  public anulacao = false;
  public contaId: number;
  public fichaId: number;
  public contaAutoComplete: EddyAutoComplete<ContaBancaria>;
  public fichaAutoComplete: EddyAutoComplete<FichaExtra>;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    private messageService: MessageService,
    protected injector: Injector,
    protected globalService: GlobalService,
    protected funcaoService: FuncaoService,
    protected contaService: ContaBancariaService,
    protected fichaService: FichaExtraService,
    protected recebimentoService: RecebimentoExtraService) {
    super(new RecebimentoExtra(), injector, RecebimentoExtra.converteJson, recebimentoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      guia: [null, [Validators.required]],
      mes: [null],
      valor_recebido: [null],
      data_recebimento: [null, [Validators.required]],
      data_referencia: [null],
      especie: ['REE', [Validators.required]],
      historico: [null, [Validators.required, Validators.minLength(2)]],
      conta: [null, [Validators.required]],
      exercicio: [this.login.exercicio, [Validators.required]],
      orgao: [this.login.orgao, [Validators.required]],
      ficha: [null, [Validators.required]],
      aux: [null],
    });
  }

  protected parametrosExtras(): {} {
    return { relations: 'orgao,exercicio,conta,conta.banco,ficha' };
  }

  protected afterInit(): void {
    this.carregarAutoCompletes();
    this.inicializarVariaveis();
  }

  protected campoFoco(): ElementRef {
    return this.inputField.inputfieldViewChild.nativeElement.focus();
  }

  protected afterLoad() {
    this.entidade.data_recebimento = new DateFormatPipe().transform(this.entidade.data_recebimento, []);
    this.entidade.data_referencia = new DateFormatPipe().transform(this.entidade.data_referencia, []);
    if (this.currentActionRoute === 'novo') {
      this.entidade.data_recebimento = new Date();
      this.entidade.data_recebimento = new Date();
      this.entidade.historico = 'RECEITA EXTRA ARRECADADA NO DIA';
      this.entidade.exercicio = this.login.exercicio;
      this.entidade.orgao = this.login.orgao;
      this.contaId = null;
      this.fichaId = null;
      this.entidade.mes = this.entidade.data_recebimento.getUTCMonth() + 1;
      this.entidade.aux = 0;
      this.entidade.especie = !this.anulacao ? 'REE' : 'REA';
    } else {
      this.anulacao = this.entidade.especie === 'REA';
      this.contaId = this.entidade.conta.id;
      this.fichaId = this.entidade.ficha.numero;
    }
  }

  protected beforeSubmit() {
    try {
      const dataRecebimento = new DateFormatPipe().transform(this.entidadeForm.get('data_recebimento').value, []);
      const dataReferencia = new DateFormatPipe().transform(this.entidadeForm.get('data_referencia').value, []);
      this.entidadeForm.get('mes').setValue(dataRecebimento.getUTCMonth() + 1);
      this.entidadeForm.get('aux').setValue(0);
      this.entidadeForm.get('especie').setValue(!this.anulacao ? 'REE' : 'REA');
      this.verificarValor();
      if (this.anulacao) {
        if (this.entidadeForm.get('data_referencia').value === null) {
          throw new Error('Infome a data de referência!');
        }
        if (dataReferencia > dataRecebimento) {
          throw new Error('Data de referência tem que ser menor que a data do recebimento!');
        }
      }

    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  protected afterSubmit(ent: RecebimentoExtra) {
    if (this.currentActionRoute === 'novo') {
      this.router.navigate(['/recebimentos-extras', 'novo', ent.id]);
    } else {
      this.router.navigate(['/recebimentos-extras', ent.id, 'editar']);
    }
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================
  private carregarAutoCompletes() {
    // autocomplete para conta
    this.contaAutoComplete = new EddyAutoComplete(this.entidadeForm.get('conta'), this.contaService,
      'id', ['numero_conta', 'banco.nome', 'nome'], { orgao_id: this.login.orgao.id, relatons: 'banco', orderBy: 'nome' },
      { number: ['numero_conta'], text: ['nome'] }
    );

    // autocomplete para ficha
    this.fichaAutoComplete = new EddyAutoComplete(this.entidadeForm.get('ficha'), this.fichaService,
      'id', ['numero', 'nome'],
      { orgao_id: this.login.orgao.id, exercicio_id: this.login.exercicio.id, relations: 'plano', orderBy: 'nome' },
      { number: ['numero'], text: ['nome'] }
    );
  }

  public changeFicha() {
    if (this.entidadeForm.get('ficha')) {
      const fh = this.entidadeForm.get('ficha').value;
      this.fichaId = fh.numero;
    }
  }

  public changeConta() {
    if (this.entidadeForm.get('conta')) {
      const conta = this.entidadeForm.get('conta').value;
      this.contaId = conta.aux;
    }
  }

  public buscarConta() {
    if (this.contaId > 0) {
      this.contaService.obterPorFicha(this.contaId, this.login.orgao.id).pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (data: any) => {
            if (data.content) {
              this.entidadeForm.get('conta').setValue(data.content[0]);
            }
          }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
        );
    }
  }

  public buscarFicha() {
    if (this.fichaId > 0) {
      this.fichaService.obterPorNumero(this.fichaId, this.login.orgao.id, this.login.exercicio.id).pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (data: any) => {
            if (data.content) {
              this.entidadeForm.get('ficha').setValue(data.content[0]);
            }
          }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
        );
    }
  }

  public blurValorRecebido() {
    this.verificarValor();
  }

  /* anulacao de receita ou ficha de redudor de receita ou ficha de restituicao de receita
  * inverte o sinal
  */
  public verificarValor() {
    const ficha: FichaExtra = this.entidadeForm.get('ficha').value;
    const valor = this.entidadeForm.get('valor_recebido').value * -1;
    if (ficha && (this.anulacao && this.entidadeForm.get('valor_recebido').value > 0)) {
      this.entidadeForm.get('valor_recebido').setValue(valor.toString());
    }
  }

  private inicializarVariaveis() {
    if (this.currentActionRoute === 'novo') {
      this.recebimentoService.ultimaData(this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((dados) => {
          this.entidade.data_recebimento = new DateFormatPipe().transform(dados, []);
          this.entidadeForm.get('data_recebimento').setValue(this.entidade.data_recebimento);
          this.entidadeForm.get('mes').setValue(this.entidade.data_recebimento.getUTCMonth() + 1);
          this.entidadeForm.get('historico').setValue('RECEITA EXTRA DO DIA');
        });
    }
  }

  public voltar() {
    switch (this.login.sistema) {
      case 'contabil':
        this.router.navigate(['/tesouraria']);
        break;
      default:
        this.sair();
        break;
    }
  }
}
